import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createSlots as _createSlots, toDisplayString as _toDisplayString, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "bg-white border border-(--border-border-base-soft) rounded-[12px] shadow-xs px-5 py-4" };
const _hoisted_2 = { class: "flex flex-wrap gap-x-5 gap-y-[2px] items-start" };
const _hoisted_3 = { class: "flex-1 min-w-[255px]" };
const _hoisted_4 = { class: "flex-1 min-w-[255px]" };
const _hoisted_5 = { class: "flex-1 min-w-[255px]" };
const _hoisted_6 = { class: "flex gap-2" };
const _hoisted_7 = { class: "flex items-center gap-2 basis-full" };
const _hoisted_8 = ["id", "for"];
const _hoisted_9 = ["id"];
const _hoisted_10 = ["id"];
const _hoisted_11 = ["id"];
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";
import { AutoComplete, Button, SelectInput, TextInput, ToggleSwitch } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { getContactById } from "../../infrastructure/adapters/contactService";
import { useContactSearch } from "@/modules/contact/composables/useContactSearch";
import { logger } from "@/infrastructure/services/LoggerService";
import CreateContactModal from "./CreateContactModal.vue";
import ContactAddEmailModal from "./ContactAddEmailModal.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'HouseholdMemberRow',
    props: {
        row: {},
        canRemove: { type: Boolean },
        position: {}
    },
    emits: ["update:role", "update:contact", "toggle-principal", "remove"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const principalSwitchId = useId();
        const principalLabelId = useId();
        const memberDetailId = useId();
        const addEmailActionId = useId();
        const removeActionId = useId();
        const memberDetail = computed(() => props.row.contactLabel
            ? t("contact.detail.relations.foyer.create.fields.isPrincipalFor", {
                contact: props.row.contactLabel,
                position: props.position,
            })
            : t("contact.detail.relations.foyer.create.fields.isPrincipalForPosition", { position: props.position }));
        // ─── Contact search ─────────────────────────────────────────────────────────────
        const { suggestions: contactSuggestions, isLoading: searchLoading, search: searchContacts, clear: clearContactSearch, } = useContactSearch();
        const showCreateContact = ref(false);
        const showAddEmail = ref(false);
        const contactOptions = computed(() => {
            const options = [...contactSuggestions.value];
            if (props.row.contactId && !options.some((o) => o.value === props.row.contactId)) {
                options.unshift({ value: props.row.contactId, label: props.row.contactLabel, image: null });
            }
            return options;
        });
        const createContactLabel = (query) => query.trim() ? t("contact.detail.relations.foyer.create.fields.createContact") : undefined;
        function onSearch(query) {
            searchContacts(query);
        }
        async function onEmailCreated() {
            if (!props.row.contactId)
                return;
            try {
                const detail = await getContactById(props.row.contactId);
                const email = detail.communicationChannels.find((c) => c.type === "Email" && c.isPrimary)?.value ?? null;
                emit("update:contact", props.row.contactId, props.row.contactLabel, email);
            }
            catch (err) {
                logger.error("HouseholdMemberRow: failed to refresh contact email after add", err);
            }
        }
        async function onContactCreated(contactId) {
            showCreateContact.value = false;
            try {
                const detail = await getContactById(contactId);
                const label = `${detail.firstName} ${detail.lastName}`.trim();
                const email = detail.communicationChannels.find((c) => c.type === "Email" && c.isPrimary)?.value ?? null;
                emit("update:contact", contactId, label, email);
            }
            catch (err) {
                logger.error("HouseholdMemberRow: failed to fetch created contact", err);
            }
        }
        function onClear() {
            clearContactSearch();
            emit("update:contact", null, "", null);
        }
        async function onContactSelected(contactId) {
            if (!contactId) {
                emit("update:contact", null, "", null);
                return;
            }
            const found = contactSuggestions.value.find((o) => o.value === contactId);
            const label = found?.label ?? "";
            try {
                const detail = await getContactById(contactId);
                const resolvedLabel = label || `${detail.firstName} ${detail.lastName}`.trim();
                const email = detail.communicationChannels.find((c) => c.type === "Email" && c.isPrimary)?.value ?? null;
                emit("update:contact", contactId, resolvedLabel, email);
            }
            catch (err) {
                logger.error("HouseholdMemberRow: failed to fetch contact email", err);
                emit("update:contact", contactId, label, null);
            }
        }
        // ─── Role options ──────────────────────────────────────────────────────────────
        const roleOptions = computed(() => [
            { value: "Father", label: t("contact.detail.relations.foyer.roles.Father") },
            { value: "Mother", label: t("contact.detail.relations.foyer.roles.Mother") },
            { value: "Child", label: t("contact.detail.relations.foyer.roles.Child") },
            { value: "Roommate", label: t("contact.detail.relations.foyer.roles.Roommate") },
            { value: "Owner", label: t("contact.detail.relations.foyer.roles.Owner") },
            { value: "Tenant", label: t("contact.detail.relations.foyer.roles.Tenant") },
            { value: "Other", label: t("contact.detail.relations.foyer.roles.Other") },
        ]);
        function onRoleSelected(val) {
            emit("update:role", val || null);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                "model-value": __props.row.contactId ?? '',
                                label: _ctx.$t('contact.detail.relations.foyer.create.fields.contact'),
                                placeholder: _ctx.$t('contact.detail.relations.foyer.create.fields.contactPlaceholder'),
                                "empty-label": _ctx.$t('contact.detail.relations.foyer.create.fields.contactEmpty'),
                                options: contactOptions.value,
                                loading: _unref(searchLoading),
                                required: true,
                                disabled: __props.row.isCurrentContact,
                                "icon-left": "",
                                "icon-right": "ri-search-line",
                                "full-width": "",
                                "create-label": createContactLabel,
                                "onUpdate:modelValue": onContactSelected,
                                onSearch: onSearch,
                                onClear: onClear,
                                onCreate: _cache[0] || (_cache[0] = ($event) => (showCreateContact.value = true))
                            }), null, 16 /* FULL_PROPS */, ["model-value", "label", "placeholder", "empty-label", "options", "loading", "disabled"])
                        ]),
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(_unref(SelectInput), {
                                "model-value": __props.row.role ?? '',
                                label: _ctx.$t('contact.detail.relations.foyer.create.fields.role'),
                                placeholder: _ctx.$t('contact.detail.relations.foyer.create.fields.rolePlaceholder'),
                                options: roleOptions.value,
                                required: true,
                                "full-width": "",
                                "onUpdate:modelValue": onRoleSelected
                            }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "options"])
                        ]),
                        _createElementVNode("div", _hoisted_5, [
                            _createVNode(_unref(TextInput), {
                                "model-value": __props.row.email ?? '',
                                label: _ctx.$t('contact.detail.relations.foyer.create.fields.memberEmail'),
                                disabled: true,
                                error: __props.row.contactId && !__props.row.email ? _ctx.$t('contact.detail.relations.foyer.errors.memberMissingEmail') : undefined,
                                "full-width": ""
                            }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                                ((__props.row.contactId && !__props.row.email) || __props.canRemove)
                                    ? {
                                        name: "actions",
                                        fn: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_6, [
                                                (__props.row.contactId && !__props.row.email)
                                                    ? (_openBlock(), _createBlock(_unref(Button), {
                                                        key: 0,
                                                        variant: "secondary",
                                                        size: "sm",
                                                        "icon-only": "",
                                                        "icon-left": "ri-mail-add-line",
                                                        "aria-labelledby": `${_unref(addEmailActionId)} ${_unref(memberDetailId)}`,
                                                        onClick: _cache[1] || (_cache[1] = ($event) => (showAddEmail.value = true))
                                                    }, null, 8 /* PROPS */, ["aria-labelledby"]))
                                                    : _createCommentVNode("v-if", true),
                                                (__props.canRemove)
                                                    ? (_openBlock(), _createBlock(_unref(Button), {
                                                        key: 1,
                                                        variant: "secondary",
                                                        size: "sm",
                                                        "icon-only": "",
                                                        "icon-left": "ri-delete-bin-line",
                                                        "aria-labelledby": `${_unref(removeActionId)} ${_unref(memberDetailId)}`,
                                                        onClick: _cache[2] || (_cache[2] = ($event) => (emit('remove')))
                                                    }, null, 8 /* PROPS */, ["aria-labelledby"]))
                                                    : _createCommentVNode("v-if", true)
                                            ])
                                        ]),
                                        key: "0"
                                    }
                                    : undefined
                            ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["model-value", "label", "error"])
                        ]),
                        _createElementVNode("div", _hoisted_7, [
                            _createVNode(_unref(ToggleSwitch), {
                                id: _unref(principalSwitchId),
                                "model-value": __props.row.isPrincipal,
                                "aria-labelledby": `${_unref(principalLabelId)} ${_unref(memberDetailId)}`,
                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => (emit('toggle-principal')))
                            }, null, 8 /* PROPS */, ["id", "model-value", "aria-labelledby"]),
                            _createElementVNode("label", {
                                id: _unref(principalLabelId),
                                for: _unref(principalSwitchId),
                                class: "cursor-pointer text-sm font-medium text-(--text-text-heading) whitespace-nowrap"
                            }, _toDisplayString(_ctx.$t("contact.detail.relations.foyer.create.fields.isPrincipal")), 9 /* TEXT, PROPS */, _hoisted_8),
                            _createElementVNode("span", {
                                id: _unref(memberDetailId),
                                hidden: ""
                            }, _toDisplayString(memberDetail.value), 9 /* TEXT, PROPS */, _hoisted_9),
                            _createElementVNode("span", {
                                id: _unref(addEmailActionId),
                                hidden: ""
                            }, _toDisplayString(_ctx.$t("contact.detail.contactInformation.addEmail")), 9 /* TEXT, PROPS */, _hoisted_10),
                            _createElementVNode("span", {
                                id: _unref(removeActionId),
                                hidden: ""
                            }, _toDisplayString(_ctx.$t("common.delete")), 9 /* TEXT, PROPS */, _hoisted_11)
                        ])
                    ])
                ]),
                _createVNode(CreateContactModal, {
                    modelValue: showCreateContact.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((showCreateContact).value = $event)),
                    "redirect-on-create": false,
                    onCreated: onContactCreated
                }, null, 8 /* PROPS */, ["modelValue"]),
                (__props.row.contactId && showAddEmail.value)
                    ? (_openBlock(), _createBlock(ContactAddEmailModal, {
                        key: 0,
                        modelValue: showAddEmail.value,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((showAddEmail).value = $event)),
                        "contact-id": __props.row.contactId,
                        "has-primary-email": false,
                        "contact-name": __props.row.contactLabel,
                        onCreated: onEmailCreated
                    }, null, 8 /* PROPS */, ["modelValue", "contact-id", "contact-name"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
